import { Box, Grid, LinearProgress, Stack, Typography } from "@mui/material";

import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import { take } from "lodash";
import { useEffect } from "react";
import type { UserRole } from "../auth/auth";
import InfiniteScroll from "../components/infiniteScroll/InfiniteScroll";
import TextSearch from "../components/textSearch/TextSearch";
import LoyaltyPointsBanner from "../loyalty/LoyaltyPointsBanner";
import { useLimit } from "../misc/useLimit";
import { useQuery } from "../misc/useQuery";
import type { RewardResponse } from "../rewards/rewardsApi";
import { useRewards } from "../rewards/useRewards";
import PublicRewardCard from "./PublicRewardCard";
import type { PublicRewardFilters } from "./usePublicRewadsSearch";

interface Props {
  readonly search: PublicRewardFilters;
  readonly onSearchChange: (value: Partial<PublicRewardFilters>) => void;
  readonly role: UserRole | null;
}

function rewardNameMatches(reward: RewardResponse, query: string) {
  return query.length === 0 || reward.name.toLowerCase().includes(query);
}

function filterRewards(rewards: RewardResponse[], query: string) {
  return rewards.filter((r) => rewardNameMatches(r, query));
}

const PublicRewardsList = ({ search, onSearchChange, role }: Props) => {
  const { limit, loadMore } = useLimit();
  const { isLoading, rewards } = useRewards();
  const searchQuery = useQuery();
  useEffect(() => {
    const query = searchQuery.get("query");
    if (query) {
      onSearchChange({
        text: query ?? "",
      });
    }
  }, []);

  const filteredRewards = filterRewards(rewards, search.text.toLowerCase());

  return (
    <Box sx={{ animation: "fadeIn 0.3s ease-in-out" }}>
      <LoyaltyPointsBanner role={role}></LoyaltyPointsBanner>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{
          alignItems: "center",
          mb: 3,
        }}
      >
        <LocalActivityIcon color="primary" />
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
          }}
        >
          Katalog nagród
        </Typography>
      </Stack>
      <TextSearch value={search.text} onChange={(q) => onSearchChange({ text: q })} />
      {isLoading && <LinearProgress />}
      <InfiniteScroll
        dataLength={limit < filteredRewards.length ? limit : filteredRewards.length}
        fetchData={loadMore}
        hasMore={limit < filteredRewards.length}
        scrollableTarget={null}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr", // 1 kolumna na telefonach
              sm: "repeat(2, 1fr)", // 2 kolumny na małych ekranach/tabletach
              md: "repeat(3, 1fr)", // 3 kolumny ułożone idealnie obok siebie na desktopie
            },
            gap: 3,
            mt: 3,
          }}
        >
          {take(filteredRewards, limit).map((reward) => (
            <Grid key={reward.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <PublicRewardCard value={reward} />
            </Grid>
          ))}
        </Box>
      </InfiniteScroll>
    </Box>
  );
};

export default PublicRewardsList;
